import {
  AlertClient,
  AlertFilterDTO,
  AlertHistoryAllDTO,
  AlertHistoryDTO,
  AlertHistoryDTO2,
  AlertHistoryDetailsDTO,
  AlertPersonDTO,
  FilteredAlertRecipientsCountRequest,
  FilteredAlertRecipientsRequest,
  PushSubscriptionDTO,
  PushTestRequest,
  SaveTemplateRequest,
  SendAlertRequest,
  TemplateDTO,
} from "./generatedFetchClient";
import { defineStore, acceptHMRUpdate } from "pinia";

export interface AlertHistoriesUiState {
  startDate: string;
  endDate: string;
  loadedStart: string;
  loadedEnd: string;
  selectedHistoryId: number;
  selectedPortalId: number | null;
  columnToSort: number;
  isSortingDown: boolean;
  scrollY: number;
}

let alertClient = new AlertClient();

// A store instance to manage alert api calls
export const useAlertStore = defineStore({
  id: "alert",
  state: () => {
    return {
      alertSubscriptions: [] as AlertPersonDTO[],
      pushSubscriptions: [] as PushSubscriptionDTO[],
      templates: [] as TemplateDTO[],
      histories: [] as AlertHistoryDTO[],
      allHistories: [] as AlertHistoryAllDTO[],
      allHistoriesUiState: null as AlertHistoriesUiState | null,
      historyDetails: [] as AlertHistoryDetailsDTO[],
      historyDetail: null as AlertHistoryDTO2 | null,
      counts: [] as number[],
    };
  },
  actions: {
    setAuthToken(token: string) {
      alertClient.setAuthToken(token);
    },
    async getAlertSubscriptions(portalId: number): Promise<AlertPersonDTO[]> {
      try {
        this.alertSubscriptions = await alertClient.getAlertSubscriptions(portalId);
        return this.alertSubscriptions;
      } catch (apiError: any) {
        console.error("error loading alert subscriptions", apiError);
        return [];
      }
    },
    async getTemplates(portalId: number): Promise<TemplateDTO[]> {
      try {
        this.templates = await alertClient.getAlertTemplates(portalId);
        return this.templates;
      } catch (apiError: any) {
        console.error("error loading alert templates", apiError);
        return [];
      }
    },
    async getHistory(portalId: number, maxResults: number): Promise<AlertHistoryDTO[]> {
      try {
        this.histories = await alertClient.getAlertHistory(portalId, maxResults);
        return this.histories;
      } catch (apiError: any) {
        console.error("error loading alert history", apiError);
        return [];
      }
    },
    async getAllHistory(
      maxResults: number,
      startDate?: string,
      endDate?: string
    ): Promise<AlertHistoryAllDTO[]> {
      try {
        this.allHistories = await alertClient.getAlertHistoryAll(
          maxResults,
          startDate ? new Date(startDate) : undefined,
          endDate ? new Date(endDate + "T23:59:59") : undefined
        );
        return this.allHistories;
      } catch (apiError: any) {
        console.error("error loading all-portals alert history", apiError);
        return [];
      }
    },
    async getHistoryDetails(AlertHistoryId: number): Promise<AlertHistoryDTO2> {
      try {
        this.historyDetail = await alertClient.getAlertHistoryDetail(AlertHistoryId);
        return this.historyDetail;
      } catch (apiError: any) {
        console.error("Error loading alert history details", apiError);
        return {} as AlertHistoryDTO2;
      }
    },
    async getEmailBounces(maxResults: number): Promise<AlertHistoryDetailsDTO[]> {
      try {
        this.historyDetails = await alertClient.getAlertHistoryByEvent(maxResults);
        return this.historyDetails;
      } catch (apiError: any) {
        console.error("Error loading alert history details", apiError);
        return [];
      }
    },
    async getFilteredCount(request: FilteredAlertRecipientsCountRequest): Promise<number> {
      try {
        return await alertClient.getFilteredAlertRecipientsCount(request);
      } catch (apiError: any) {
        console.error("error getting alert recipients");
        return -1;
      }
    },
    async getFilteredRecipients(
      request: FilteredAlertRecipientsRequest
    ): Promise<AlertFilterDTO[]> {
      try {
        return await alertClient.getFilteredAlertRecipients(request);
      } catch (apiError: any) {
        console.error("error getting alert recipients");
        return [];
      }
    },
    // async getCrossBuildingCount(request: FilteredAlertRecipientsCountRequest): Promise<number> {
    //   try {
    //     return await alertClient.getCrossBuildingCount(request);
    //   } catch (apiError: any) {
    //     console.error("error getting cross-building alert recipients");
    //     return -1;
    //   }
    // },  

    async saveTemplate(request: SaveTemplateRequest): Promise<string> {
      try {
        await alertClient.saveTemplate(request);
        return "";
      } catch (apiError: any) {
        console.error(apiError);
        return apiError;
      }
    },
    async deleteTemplate(templateId: number, portalId: number): Promise<string> {
      try {
        await alertClient.deleteTemplate(templateId, portalId);
        return "";
      } catch (apiError: any) {
        console.error(apiError);
        return apiError;
      }
    },
    // AI short version of an alert that fits in one SMS part; null on failure
    async summarizeAlert(portalId: number, message: string): Promise<string | null> {
      try {
        const result = await alertClient.summarizeAlert({ portalId, message });
        return result.summary ?? null;
      } catch (apiError: any) {
        console.error("error summarizing alert message", apiError);
        return null;
      }
    },
    async sendAlert(request: SendAlertRequest): Promise<string | null> {
      try {
        // if (isProduction()) {
        //   //TODO: migrate prod to new send engine after testing
        //   console.log("Sending using Old Direct Alert engine", request);
        //   await alertClient.sendAlertDirect2(request);
        // } else {
        console.log("Sending using Async Alert engine", request);
        await alertClient.sendAlert2(request);
        //}
        return "";
      } catch (apiError: any) {
        console.error(apiError);
        return apiError?.response ? apiError.response : apiError.toString() + apiError.status;
      }
    },
    async getAlertCounts(): Promise<string> {
      try {
        let alertCountsDTO = await alertClient.getCounts();
        this.counts = alertCountsDTO.counts ?? [];

        return alertCountsDTO.smsSpendThisMonth ?? "";
      } catch (apiError: any) {
        console.error(apiError);
        return "";
      }
    },
    async unsubscribe(email: string): Promise<string> {
      try {
        await alertClient.unsubscribe(email);
        return "";
      } catch (apiError: any) {
        console.error(apiError);
        return apiError;
      }
    },
    async optIn(phoneNumber: string): Promise<string> {
      try {
        await alertClient.optIn(phoneNumber);
        return "";
      } catch (apiError: any) {
        console.error(apiError.response);
        return apiError.response;
      }
    },

    async updateNotificationToken(userName: string, token: string | null): Promise<boolean> {
      if (userName) {
        try {
          await alertClient.updateNotificationToken(navigator.userAgent, token );
          console.log("Updated notification token on server", token);
          return true;
        } catch (apiError: any) {
          console.error("Error updating notification token", apiError);
        }
      }
      return false;
    },

    async setPushOptOut(isOptedOut: boolean): Promise<string> {
      try {
        await alertClient.setPushOptOut(isOptedOut);
        return "";
      } catch (apiError: any) {
        console.error("Error setting push opt-out", apiError);
        return apiError?.response ? apiError.response : apiError.toString();
      }
    },

    async getPushSubscriptions(personId: number): Promise<PushSubscriptionDTO[]> {
      try {
        this.pushSubscriptions = await alertClient.getPushSubscriptions(personId);
        return this.pushSubscriptions;
      } catch (apiError: any) {
        console.error("error loading push subscriptions", apiError);
        return [];
      }
    },
    async deletePushSubscription(pushSubscriptionId: number): Promise<string> {
      try {
        await alertClient.deletePushSubscription(pushSubscriptionId);
        return "";
      } catch (apiError: any) {
        console.error("error deleting push subscription", apiError);
        return apiError;
      }
    },
    async sendTestPushNotification(request: PushTestRequest): Promise<string> {
      try {
        await alertClient.sendTestPushNotification(request);
        return "";
      } catch (apiError: any) {
        console.error("error sending test push notification", apiError);
        return apiError?.response ? apiError.response : apiError.toString();
      }
    },

  },
  getters: {},
});

// function isProduction() {
//   return location.host == "aksafetynet.com";
// }

//Enable Hot Module Replacement
// https://pinia.esm.dev/cookbook/hot-module-replacement.html
if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(acceptHMRUpdate(useAlertStore as any, (import.meta as any).hot));
}
